import { BrainSearchMessageInternalSourceFragment$key } from "@/brain-search/internal/__generated__/BrainSearchMessageInternalSourceFragment.graphql"
import CommunityBadge from "@/community/CommunityBadge"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import LockIcon from "@/core/ui/iconsax/bold/lock.svg"
import CertifiedBadge from "@/core/ui/iconsax/bold/verify.svg"
import SlackIcon from "@/core/ui/images/logos/slack-icon.svg"
import ProfileAvatar from "@/user/common/avatar/ProfileAvatar"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoText, DiscoTextSkeleton } from "@disco-ui"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import { stripURL } from "@utils/string/stringUtils"
import { TestIDProps } from "@utils/typeUtils"
import { getFaviconUrl, isValidUrl } from "@utils/url/urlUtils"
import classNames from "classnames"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"

export const BRAIN_SEARCH_SOURCE_MIN_WIDTH = "192px"

interface Props extends TestIDProps {
  brainSearchMessageSourceKey: BrainSearchMessageInternalSourceFragment$key
  citation?: string // Letter citation: "A", "B", "C"
}

function BrainSearchMessageInternalSource({
  brainSearchMessageSourceKey,
  citation,
}: Props) {
  const source = useFragment<BrainSearchMessageInternalSourceFragment$key>(
    graphql`
      fragment BrainSearchMessageInternalSourceFragment on BrainSearchMessageSource {
        id
        entity
        entityId
        drawerSearchParams {
          contentId
          assetId
          postId
          u
          drawerOccurrenceId
        }
        content {
          id
          name
        }
        asset {
          id
          name
        }
        trainingData {
          id
          name
          url
        }
        botContext {
          link
          platform
          originalThreadMessageText
          originalThreadMessageUser {
            fullName
            ...ProfileAvatarFragment
          }
        }
      }
    `,
    brainSearchMessageSourceKey
  )

  const handleClickSource = getClickSourceHandler()
  const isLocked = !handleClickSource // Sources are locked if they don't have access to it anymore

  const isUnavailable =
    (source.entity === "content" && !source.content) ||
    (source.entity === "asset" && !source.asset) ||
    (source.entity === "training_data" && !source.trainingData)

  const activeOrganization = useActiveOrganization()
  const classes = useStyles({ isLocked, isUnavailable })
  const brainSearchDrawer = useGlobalDrawer("brainSearch")
  const contentUsageDrawer = useGlobalDrawer("contentUsage")
  const drawer = contentUsageDrawer.params.drawerAsk
    ? contentUsageDrawer
    : brainSearchDrawer

  return (
    <DiscoContainerButton
      key={source.id}
      disabled={isLocked || isUnavailable}
      tooltip={
        isUnavailable ? "The source content may have been moved or deleted." : undefined
      }
      className={classNames(classes.source, classes.fadeIn)}
      onClick={handleClickSource}
    >
      <div className={classes.topSource}>
        <DiscoText variant={"body-xs-700"} color={"text.primary"} truncateText={2}>
          {getTitle()}
        </DiscoText>
        {getTopRightIcon()}
      </div>
      <div className={classes.bottomSource}>
        <div className={classes.sourceContent}>
          {getBottomLeftIcon()}
          {getBottomLeftText()}
        </div>
        {getBottomRightIcon()}
      </div>
    </DiscoContainerButton>
  )

  function getTitle() {
    let title = ""

    if (isUnavailable) {
      title = "No longer available"
    } else {
      switch (source.entity) {
        case "content":
          title = source.content?.name || "Content"
          break
        case "asset":
          title = source.asset?.name || "Asset"
          break
        case "training_data":
          title = source.trainingData?.name || "Training Data"
          break
        case "bot_context":
          title = source.botContext?.originalThreadMessageText || "Chat Thread"
          break
        default:
          title = "Source"
      }
    }

    // Add citation prefix if provided
    return citation ? `[${citation}] ${title}` : title
  }

  function getTopRightIcon() {
    if (isLocked) {
      return <LockIcon className={classes.icon} width={16} height={16} />
    }

    if (isUnavailable) {
      return null
    }

    return source.entity === "bot_context" ? null : (
      <CertifiedBadge
        className={classNames(classes.icon, classes.certified)}
        width={16}
        height={16}
      />
    )
  }

  function getBottomLeftIcon() {
    switch (source.entity) {
      case "bot_context": {
        if (source.botContext?.originalThreadMessageUser) {
          return (
            <ProfileAvatar
              userKey={source.botContext.originalThreadMessageUser}
              size={20}
            />
          )
        }
        return <DiscoIcon width={20} height={20} icon={"iconsax.profile-circle"} />
      }
      case "training_data": {
        if (source.trainingData?.url) {
          return (
            <img
              alt={""}
              src={getFaviconUrl(source.trainingData.url)}
              className={classes.favIcon}
            />
          )
        }
        return null
      }
      default: {
        if (activeOrganization) {
          return (
            <CommunityBadge
              organizationKey={activeOrganization}
              size={20}
              classes={{ badge: classes.badge }}
            />
          )
        }
        return null
      }
    }
  }

  function getBottomLeftText() {
    switch (source.entity) {
      case "bot_context": {
        return (
          <DiscoText variant={"body-xs"} color={"text.secondary"} truncateText={1}>
            {source.botContext?.originalThreadMessageUser?.fullName ||
              (source.botContext?.platform === "slack" ? "Slack User" : "Disco User")}
          </DiscoText>
        )
      }
      case "training_data": {
        if (source.trainingData?.url) {
          return (
            <DiscoText variant={"body-xs"} color={"text.secondary"} truncateText={1}>
              {stripURL(
                isValidUrl(source.trainingData.url) ? source.trainingData.url : undefined
              ) || "External Website"}
            </DiscoText>
          )
        }
        return null
      }
      default: {
        return (
          <DiscoText variant={"body-xs"} color={"text.secondary"} truncateText={1}>
            {activeOrganization?.name}
          </DiscoText>
        )
      }
    }
  }

  function getBottomRightIcon() {
    switch (source.entity) {
      case "bot_context": {
        if (source.botContext?.platform === "slack") {
          return <SlackIcon width={16} height={16} />
        }
        return <DiscoIcon width={16} height={16} icon={"iconsax.hashtag-1"} />
      }
      default: {
        return null
      }
    }
  }

  function getClickSourceHandler() {
    if (isUnavailable) {
      return undefined
    }
    switch (source.entity) {
      case "content":
      case "asset":
        return handleClickContentOrAssetSource()
      case "training_data":
        return handleClickTrainingDataSource()
      case "bot_context":
        return handleClickBotContextSource()
      default:
        return undefined
    }
  }

  function handleClickBotContextSource() {
    if (source.botContext?.link) {
      return () => window.open(source.botContext!.link!, "_blank")
    }
    return undefined
  }

  function handleClickTrainingDataSource() {
    if (source.trainingData?.url) {
      return () => window.open(source.trainingData!.url, "_blank")
    }
    return undefined
  }

  function handleClickContentOrAssetSource() {
    const { drawerSearchParams } = source
    if (drawerSearchParams?.contentId) {
      return () => {
        drawer.swap<"adminContent">({
          contentId: drawerSearchParams.contentId!,
          return: "ask",
        })
      }
    } else if (drawerSearchParams?.assetId) {
      return () => {
        drawer.swap<"asset">({
          assetId: drawerSearchParams.assetId!,
          return: "ask",
        })
      }
    } else if (drawerSearchParams?.postId) {
      return () => {
        drawer.swap<"post">({
          postId: drawerSearchParams.postId!,
          return: "ask",
        })
      }
    } else if (drawerSearchParams?.u) {
      return () => {
        drawer.swap<"contentUsage">({
          u: drawerSearchParams.u!,
          return: "ask",
        })
      }
    } else if (drawerSearchParams?.drawerOccurrenceId) {
      return () => {
        drawer.swap<"event">({
          drawerOccurrenceId: drawerSearchParams.drawerOccurrenceId!,
          drawerEventTab: "details",
          return: "ask",
        })
      }
    }
    return undefined
  }
}

const useStyles = makeUseStyles((theme) => ({
  source: ({
    isLocked,
    isUnavailable,
  }: {
    isLocked: boolean
    isUnavailable: boolean
  }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.measure.borderRadius.big,
    backgroundColor: theme.palette.groovy.neutral[100],
    border: `1px solid ${theme.palette.groovy.neutral[100]}`,
    minWidth: BRAIN_SEARCH_SOURCE_MIN_WIDTH,
    minHeight: "96px",

    "&:hover": {
      borderColor: theme.palette.groovy.neutral[300],
      backgroundColor: theme.palette.groovy.neutral[200],
      boxShadow:
        "0px 2px 4px 0px rgba(63, 63, 70, 0.04), 0px 6px 18px 0px rgba(63, 63, 70, 0.08)",
    },
    ...(isLocked || isUnavailable
      ? {
          cursor: "default",
          pointerEvents: "none",
        }
      : {
          cursor: "pointer",
          pointerEvents: "auto",
        }),
    ...(isUnavailable && { opacity: 0.5 }),
  }),
  topSource: {
    width: "100%",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexDirection: "row",
    display: "flex",
    flex: 1,
    gap: theme.spacing(1),
  },
  icon: {
    right: 0,
    flexShrink: 0,
  },
  certified: {
    color: theme.palette.primary.main,
  },
  bottomSource: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  sourceContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    width: "100%",
  },
  badge: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
  },
  fadeIn: {
    animation: "$fadeIn 1s",
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
  favIcon: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background:
      "radial-gradient(100% 100% at 50% 0%, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%), var(--Colors-blue-600, #4D47DC)",
  },
}))

export function BrainSearchMessageSourceSkeleton() {
  const classes = useStyles()

  return (
    <DiscoContainerButton className={classes.source}>
      <div className={classes.topSource}>
        <DiscoTextSkeleton width={"100%"} />
      </div>
      <div className={classes.bottomSource}>
        <div className={classes.sourceContent}>
          <div className={classes.badge} />
          <DiscoTextSkeleton width={"50%"} />
        </div>
      </div>
    </DiscoContainerButton>
  )
}

export default observer(BrainSearchMessageInternalSource)
